import { Task, Timestamp, UpdateTask } from '@griply/types';
import { getDateFormat, getWeekStartsOn } from '@griply/utils';
import {
  buildDateUpdateTasks,
  getSharedDateValue,
  useUpdateTasks,
} from 'features/tasks';
import React, { useMemo } from 'react';
import {
  DatepickerWithOptions,
  DatePresets,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { getPlatformShortcut } from 'shared/utils/get-platform-shortcut';

export interface DateSubMenuProps
  extends Omit<
    React.ComponentProps<typeof PopupMenu>,
    'children' | 'onOutside'
  > {
  tasks: Task[];
  onClose: () => void;
  onUpdate?: (task: Task, updates: UpdateTask) => void;
}

const datePresets = [
  DatePresets.Today,
  DatePresets.Tomorrow,
  DatePresets.NextWeek,
];

export const DateSubMenu: React.FC<DateSubMenuProps> = ({
  tasks,
  onClose,
  onUpdate,
  ...rest
}) => {
  const today = useToday();
  const { submit: updateTasks } = useUpdateTasks();
  const user = useUser();
  const dateFormat = getDateFormat(user);
  const weekStartsOn = getWeekStartsOn(user);

  const value = useMemo<Timestamp | undefined>(
    () => getSharedDateValue(tasks, today),
    [tasks, today],
  );

  const onChange = (date?: Timestamp) => {
    const tasksToUpdate = buildDateUpdateTasks(tasks, date, value, {
      weekStartsOn,
      today,
    });

    if (!tasksToUpdate.length) {
      onClose();
      return;
    }

    if (onUpdate) {
      tasksToUpdate.forEach((update) => {
        const task = tasks.find(({ id }) => id === update.id);
        if (task) {
          onUpdate(task, update);
        }
      });
      onClose();
      return;
    }

    updateTasks(tasksToUpdate);
    onClose();
  };

  const shortcuts = useMemo(
    () => ({
      [DatePresets.Today]: getPlatformShortcut(['Shift', 'T']),
      [DatePresets.Tomorrow]: getPlatformShortcut(['Alt', 'Shift', 'T']),
      [DatePresets.NextWeek]: getPlatformShortcut(['Shift', 'W']),
    }),
    [],
  );

  return (
    <DatepickerWithOptions
      {...rest}
      value={value}
      onChange={onChange}
      dateFormat={dateFormat}
      weekStartsOn={weekStartsOn}
      datePresets={datePresets}
      icon={IconOptions.Calendar}
      onOutside={onClose}
      canRemoveValue
      forceRemoveValueOption
      shortcuts={shortcuts}
      clearShortcut={getPlatformShortcut(['Shift', 'U'])}
    />
  );
};
